import React, { useMemo, useState, useEffect, useRef } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

type EmployeeRow = {
  id: string;
  empNo: string;
  name: string;
  category?: string;
  designation: string;
  departmentName?: string;
  joiningDate?: string;
  lastPromoDate?: string;
  joiningOrLastPromoDate?: string;
  serviceYears: number | string;
  aparTotal: string;
  aparRemarks?: string;
  dar: string;
  vigilance: string;
  effectiveDate: string;
  listClass: string;
  dpcRemarks?: string;
  dpcStatus?: string;
  [key: string]: any;
};

const formatAparScore = (val: any): string => {
  if (val === undefined || val === null || val === "" || val === "—") return "-";
  const str = String(val).trim();
  const num = parseFloat(str);
  if (!isNaN(num) && num === 0) return "-";
  if (str === "0" || str === "0.0" || str === "0.00") return "-";
  return str;
};

const PromotionList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const stateCycleId = location.state?.cycleId;
  const paramCycleId = queryParams.get("cycleId");
  const initialCycleId = stateCycleId || (paramCycleId ? Number(paramCycleId) : 1);

  const [selectedCycleId, setSelectedCycleId] = useState<number>(initialCycleId);
  const [activeList, setActiveList] = useState<string>("MasterData");
  const [selectedCandidateForInclude, setSelectedCandidateForInclude] = useState<EmployeeRow | null>(null);
  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"apar" | "dar" | "vigilance" | "exclude">("apar");
  const [includeRemarks, setIncludeRemarks] = useState<string>("");
  const [remarksError, setRemarksError] = useState<boolean>(false);
  const [includingEmpId, setIncludingEmpId] = useState<string | null>(null);
  const [query, setQuery] = useState<string>("");
  const [masterStatusFilter, setMasterStatusFilter] = useState<string>("ALL");
  const [masterDarFilter, setMasterDarFilter] = useState<string>("ALL");
  const [masterVigilanceFilter, setMasterVigilanceFilter] = useState<string>("ALL");
  const [masterEmployees, setMasterEmployees] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cyclesList, setCyclesList] = useState<any[]>([
    { id: 1, name: " " }
  ]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const currentCycle = useMemo(() => {
    return cyclesList.find((c) => c.id === selectedCycleId);
  }, [cyclesList, selectedCycleId]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, activeList, query, masterStatusFilter, masterDarFilter, masterVigilanceFilter]);



  // Load initiated cycles list from API or fallback to localStorage
  useEffect(() => {
    const loadCycles = async () => {
      let loaded = false;
      try {
        const response = await Api.get(API_URLS.PROMOTION.CYCLES, true);
        console.log("CYCLES API Response (Promotion List):", response);
        if (response && response.data && Array.isArray(response.data.data)) {
          const rawList = response.data.data;
          const list = rawList.map((c: any) => ({
            id: c.id,
            name: c.cycleName || `${c.gradeFrom} to ${c.gradeTo} Promotion - ${c.quarter} ${c.financialYear}`,
            status: c.status,
            financialYear: c.financialYear,
            quarter: c.quarter,
            grades: c.grades,
            cadre: c.cadre,
            cycleName: c.cycleName
          }));
          setCyclesList(list);
          loaded = true;

          if (list.length > 0) {
            const queryId = queryParams.get("cycleId");
            if (!queryId && !location.state?.cycleId) {
              setSelectedCycleId(list[0].id);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch cycles from API, falling back to local storage", error);
      }

      if (!loaded) {
        const stored = localStorage.getItem("promotion_cycles");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const hasCycle1 = parsed.some((c: any) => c.id === 1);
              const list = hasCycle1 ? parsed : [{ id: 1, name: " " }, ...parsed];
              setCyclesList(list);
            }
          } catch (e) {
            console.error("Failed to parse promotion cycles from localStorage", e);
          }
        }
      }
    };
    loadCycles();
  }, []);

  // Sync state if URL / state changes externally
  useEffect(() => {
    const stateId = location.state?.cycleId;
    const queryId = queryParams.get("cycleId");
    const currentId = stateId || (queryId ? Number(queryId) : 1);
    if (currentId !== selectedCycleId) {
      setSelectedCycleId(currentId);
    }
  }, [location.state, location.search]);

  // Fetch employees checklist for the selected cycle
  const fetchChecks = async () => {
    setLoading(true);
    try {
      // 1. Fetch Master Data from EVALUATE_TWO_STAGE (eligibleByServiceWithEvaluations)
      const masterPayload = {
        Request: {
          data: {
            cycleId: Number(selectedCycleId)
          }
        }
      };
      const masterPromise = Api.post(API_URLS.PROMOTION.EVALUATE_TWO_STAGE, masterPayload, true);

      // 2. Fetch Lists (List A-E, Eligible) from ELIGIBLE_LIST
      const listsPromise = Api.post(API_URLS.PROMOTION.ELIGIBLE_LIST, {
        cycleId: Number(selectedCycleId),
        page: 1,
        limit: 100
      }, true);

      const [masterRes, listsRes] = await Promise.allSettled([masterPromise, listsPromise]);

      if (masterRes.status === "fulfilled" && masterRes.value) {
        const response = masterRes.value;
        const resData = response?.data || response;
        const innerData = resData?.data || resData;
        let masterDataToSet: any[] = [];
        if (Array.isArray(innerData?.masterCandidates)) {
          masterDataToSet = innerData.masterCandidates;
        } else if (innerData && Array.isArray(innerData.eligibleByServiceWithEvaluations)) {
          masterDataToSet = innerData.eligibleByServiceWithEvaluations;
        } else if (resData && Array.isArray(resData.eligibleByServiceWithEvaluations)) {
          masterDataToSet = resData.eligibleByServiceWithEvaluations;
        } else if (innerData?.data && Array.isArray(innerData.data.eligibleByServiceWithEvaluations)) {
          masterDataToSet = innerData.data.eligibleByServiceWithEvaluations;
        } else if (Array.isArray(innerData)) {
          masterDataToSet = innerData;
        } else if (innerData && Array.isArray(innerData.data)) {
          masterDataToSet = innerData.data;
        } else if (resData && Array.isArray(resData.data)) {
          masterDataToSet = resData.data;
        } else if (typeof innerData === 'object' && innerData !== null) {
          const rawMerged: any[] = [];
          Object.keys(innerData).forEach(key => {
            if (Array.isArray(innerData[key])) {
              rawMerged.push(...innerData[key]);
            }
          });
          masterDataToSet = rawMerged;
        }
        setMasterEmployees(masterDataToSet);
      } else {
        setMasterEmployees([]);
      }

      if (listsRes.status === "fulfilled" && listsRes.value) {
        const response = listsRes.value;
        let dataToSet: any[] = [];
        if (response && response.data) {
          const rootData = response.data;
          if (Array.isArray(rootData)) {
            dataToSet = rootData;
          } else {
            const innerData = rootData.data || rootData;
            if (Array.isArray(innerData?.masterCandidates)) {
              dataToSet = innerData.masterCandidates;
            } else if (Array.isArray(innerData)) {
              dataToSet = innerData;
            } else if (innerData.data && Array.isArray(innerData.data)) {
              dataToSet = innerData.data;
            } else if (typeof innerData === 'object' && innerData !== null) {
              const rawMerged: any[] = [];
              Object.keys(innerData).forEach(key => {
                if (key.endsWith('_Count') || key.endsWith('Count')) return;
                if (key === 'eligibleListDpc' && innerData.eligibleList) return;

                if (Array.isArray(innerData[key])) {
                  rawMerged.push(...innerData[key]);
                }
              });

              const uniqueMap = new Map();
              rawMerged.forEach((item: any) => {
                const uKey = item.empId || item.emp_no || item.id || JSON.stringify(item);
                if (!uniqueMap.has(uKey)) {
                  uniqueMap.set(uKey, item);
                }
              });
              dataToSet = Array.from(uniqueMap.values());
            }
          }
        }
        setEmployees(dataToSet);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Failed to fetch checks", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecks();
  }, [selectedCycleId]);

  const handleCycleChange = (id: number) => {
    setSelectedCycleId(id);
    navigate(`${location.pathname}?cycleId=${id}`, { replace: true, state: { cycleId: id } });
  };

  const formatDateDMY = (dateStr: any) => {
    if (!dateStr || dateStr === "N/A" || dateStr === "-" || dateStr === "null" || dateStr === "undefined") return "—";
    const str = String(dateStr).trim();
    if (!str) return "—";
    if (str.includes("T")) {
      const ymd = str.split("T")[0];
      const parts = ymd.split("-");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (str.includes("-")) {
      const parts = str.split("-");
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      if (parts.length === 3 && parts[2].length === 4) return str;
    }
    return str;
  };

  const getEffectivePromotionDate = (e: any) => {
    const raw =
      e.adjustedPromotionDate ??
      e.adjusted_promotion_date ??
      e.effectivePromotionDate ??
      e.effective_promotion_date ??
      e.effectiveEligibilityDate ??
      e.effective_eligibility_date ??
      e.eligibilityDate ??
      e.baseDate ??
      e.base_date ??
      e.promotion_effective_date ??
      e.promotionEffectiveDate ??
      e.effectiveDate;

    if (!raw || raw === "N/A" || raw === "null" || raw === "undefined" || raw === "-") return "—";
    return formatDateDMY(raw);
  };

  const getDarBadgeDetails = (e: any) => {
    const rawStatus = (e.darStatus ?? e.dar_status ?? e.dar_evaluation?.status ?? e.dar ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "DAR CLEAR",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
      return {
        text: "ChargeSheet Issued",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
      return {
        text: "Minor Penalty",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
      return {
        text: "Major Penalty",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getVigilanceBadgeDetails = (e: any) => {
    const rawStatus = (e.vigilance_evaluation?.status ?? e.vigilanceStatus ?? e.vigilance_status ?? e.vigilanceType ?? e.vigilance_type ?? e.vigilance ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "no vigilance case" || statusLower === "no_vigilance_case" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "No Vigilance Case",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "vigilance initiated" || statusLower === "vigilance_initiated") {
      return {
        text: "Vigilance Initiated",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "penalty awarded" || statusLower === "penalty_awarded") {
      return {
        text: "Penalty Awarded",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const formatEmployeeRow = (e: any, idx: number): EmployeeRow => {
    let joiningDate = e.joiningDate || e.joining_date || e.date_of_joining || e.dateOfJoining || e.doj || "";
    let lastPromoDate = e.lastPromoDate || e.last_promotion_date || e.last_promo_date || e.lastPromotion || "";
    const combined = e.joiningOrLastPromoDate || e.joining_or_last_promo_date || "";

    if ((!joiningDate || !lastPromoDate) && combined) {
      if (typeof combined === "string" && combined.includes("/")) {
        const parts = combined.split("/").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (typeof combined === "string" && combined.includes(",")) {
        const parts = combined.split(",").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (!joiningDate) {
        joiningDate = combined;
      }
    }

    const formatJoiningDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "—";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    const formatLastPromoDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "No Data Found";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    let category =
      e.category ||
      e.emp_category ||
      e.empCategory ||
      e.caste_category ||
      e.casteCategory ||
      e.cast_category ||
      e.castCategory ||
      e.caste ||
      e.social_category ||
      e.socialCategory ||
      "—";
    if (category === "N/A" || category === "null" || category === "undefined") category = "—";

    let desig = e.oldDesignation || e.old_designation || e.designationName || e.designation || e.newDesignation || e.new_designation || e.designationID || "—";
    if (typeof desig === "string" && desig.includes("-")) {
      const parts = desig.split("-").filter((p: string) => p !== "P");
      if (parts.length > 0) desig = parts.join("-");
    }

    let aparTotal = "—";
    if (e.apar_evaluation?.totalScore !== undefined && e.apar_evaluation?.totalScore !== null) {
      aparTotal = String(e.apar_evaluation.totalScore);
    } else if (e.apar_evaluation?.total_score !== undefined && e.apar_evaluation?.total_score !== null) {
      aparTotal = String(e.apar_evaluation.total_score);
    } else if (e.aparTotalScore !== undefined && e.aparTotalScore !== null) {
      aparTotal = String(e.aparTotalScore);
    } else if (e.apar_total_score !== undefined && e.apar_total_score !== null) {
      aparTotal = String(e.apar_total_score);
    }

    const lastPromoDateVal = formatLastPromoDate(lastPromoDate);
    const candidateKey = e.candidateId ? String(e.candidateId) : (e.id ? String(e.id) : (e.empId || e.emp_no || String(idx)));

    return {
      ...e,
      key: candidateKey,
      id: candidateKey,
      empNo: e.empId || e.emp_no || e.empNo || "—",
      name: e.employeeName || e.empName || e.emp_name || "—",
      category: String(category),
      designation: String(desig),
      departmentName: String(e.departmentName || e.department_name || "-"),
      joiningDate: formatJoiningDate(joiningDate),
      lastPromoDate: lastPromoDateVal !== "No Data Found" ? lastPromoDateVal : (e.joiningOrLastPromoDate || "No Data Found"),
      joiningOrLastPromoDate: e.joiningOrLastPromoDate || "—",
      serviceYears: e.serviceYears !== undefined && e.serviceYears !== null ? String(e.serviceYears) : (e.service_years !== undefined && e.service_years !== null ? String(e.service_years) : "—"),
      aparTotal: aparTotal,
      aparRemarks: e.aparRemarks ?? e.apar_remarks ?? e.aparRemark ?? e.apar_remark ?? "—",
      effectiveDate: getEffectivePromotionDate(e),
      listClass: e.listClassification || e.list_classification || e.finalDecision || e.status || "",
      dpcStatus: e.dpcStatus || e.dpc_status || "PENDING",
      dpcRemarks: e.dpcRemarks || e.dpc_remarks || e.remarks || e.reason || ""
    };
  };

  const parsedMasterEmployees = useMemo(() => {
    if (!Array.isArray(masterEmployees)) return [];
    return masterEmployees.map((e: any, idx: number) => formatEmployeeRow(e, idx));
  }, [masterEmployees]);

  const parsedEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    // DPC review status must not remove an employee from List A-E or Eligible.
    // These tabs represent check classification, which remains visible after
    // recommendation and Competent Authority decisions for audit purposes.
    return employees.map((e: any, idx: number) => formatEmployeeRow(e, idx));
  }, [employees]);

  const listsData = useMemo(() => {
    const groups: Record<string, EmployeeRow[]> = {
      MasterData: parsedMasterEmployees.length > 0 ? parsedMasterEmployees : [],
      ListA: [],
      ListB: [],
      ListC: [],
      ListD: [],
      Eligible: []
    };
    parsedEmployees.forEach((e: EmployeeRow) => {
      if (parsedMasterEmployees.length === 0) {
        groups.MasterData.push(e);
      }
      const cls = (e.listClass || "").toUpperCase();
      if (cls === "ELIGIBLE_DPC" || cls === "ELIGIBLE") groups.Eligible.push(e);
      else if (cls === "LIST_A") groups.ListA.push(e);
      else if (cls === "LIST_B") groups.ListB.push(e);
      else if (cls === "LIST_C") groups.ListC.push(e);
      else if (cls === "LIST_D" || cls === "LIST_D_E" || cls === "LIST_E") groups.ListD.push(e);
      else groups.Eligible.push(e);
    });
    return groups;
  }, [parsedMasterEmployees, parsedEmployees]);

  const filtered = useMemo(() => {
    let currentList = listsData[activeList] || [];
    if (activeList === "MasterData") {
      if (masterStatusFilter && masterStatusFilter !== "ALL") {
        currentList = currentList.filter((e: EmployeeRow) => {
          const cls = (e.listClass || "").toUpperCase();
          if (masterStatusFilter === "ELIGIBLE_DPC") return cls === "ELIGIBLE_DPC" || cls === "ELIGIBLE";
          if (masterStatusFilter === "LIST_D") return cls === "LIST_D" || cls === "LIST_D_E" || cls === "LIST_E";
          return cls === masterStatusFilter;
        });
      }
      if (masterDarFilter && masterDarFilter !== "ALL") {
        currentList = currentList.filter((e: EmployeeRow) => e.dar === masterDarFilter);
      }
      if (masterVigilanceFilter && masterVigilanceFilter !== "ALL") {
        currentList = currentList.filter((e: EmployeeRow) => e.vigilance === masterVigilanceFilter);
      }
    }
    if (!query) return currentList;
    const q = query.toLowerCase();
    return currentList.filter((e: EmployeeRow) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.empNo || "").toLowerCase().includes(q) ||
      (e.designation || "").toLowerCase().includes(q) ||
      (e.departmentName || "").toLowerCase().includes(q)
    );
  }, [query, activeList, listsData, masterStatusFilter, masterDarFilter, masterVigilanceFilter]);

  const handleExportMasterExcel = () => {
    const dataToExport = activeList === "MasterData" ? filtered : listsData.MasterData;
    if (!dataToExport || dataToExport.length === 0) {
      toast.warning("No Master Data available to export.");
      return;
    }

    const excelData = dataToExport.map((item: EmployeeRow, index: number) => ({
      "S.No": index + 1,
      "Emp No": item.empNo,
      "Employee Name": item.name,
      "Category": item.category,
      "Designation": item.designation,
      "Employee Department": item.departmentName,
      "Exclusion Reason": item.exclusionReason || "",
      "Date of Joining": item.joiningDate,
      "Last Promotion Date": item.lastPromoDate,
      "Service (Years)": item.serviceYears,
      "APAR Total Score": formatAparScore(item.aparTotal),
      "APAR Remarks": item.aparRemarks || item.apar_remarks || item.aparRemark || item.apar_remark || "—",
      "DAR Status": item.dar,
      "Vigilance Status": item.vigilance,
      "Promotion Effective Date": item.effectiveDate,
      "Classification / List": item.listClass || "-",
      "Decision / Remarks": item.dpcRemarks || "-"
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 25 },
      { wch: 12 },
      { wch: 30 },
      { wch: 14 },
      { wch: 18 },
      { wch: 15 },
      { wch: 16 },
      { wch: 25 },
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
      { wch: 22 },
      { wch: 35 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Master Data");

    const cycleNameClean = currentCycle?.name ? currentCycle.name.replace(/[^a-zA-Z0-9_-]/g, "_") : `Cycle_${selectedCycleId}`;
    const dateStr = new Date().toISOString().split("T")[0];
    const fileName = `Master_Data_${cycleNameClean}_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    toast.success(`Exported ${excelData.length} Master Data records to Excel!`);
  };

  // Section Configuration for Cards and Info with Icons and Themes
  const promotionOverview = [
    {
      key: "MasterData",
      title: "Master Data",
      subtitle: "All Candidates",
      count: listsData.MasterData.length,
      color: "#1e40af",
      bgColor: "#eff6ff",
      icon: "database",
      textColor: "text-primary"
    },
    {
      key: "ListA",
      title: "List A",
      subtitle: "Service Shortfall / Exclusion / Missing Latest APAR",
      count: listsData.ListA.length,
      color: "#ef4444",
      bgColor: "#fef2f2",
      icon: "clock",
      textColor: "text-danger"
    },
    {
      key: "ListB",
      title: "List B",
      subtitle: "APAR Short",
      count: listsData.ListB.length,
      color: "#f59e0b",
      bgColor: "#fefbeb",
      icon: "file-text",
      textColor: "text-warning"
    },
    {
      key: "ListC",
      title: "List C",
      subtitle: "DAR Pending",
      count: listsData.ListC.length,
      color: "#f97316",
      bgColor: "#fff7ed",
      icon: "scale",
      textColor: "text-orange"
    },
    {
      key: "ListD",
      title: "List D",
      subtitle: "Vigilance / Criminal Cases",
      count: listsData.ListD.length,
      color: "#a855f7",
      bgColor: "#faf5ff",
      icon: "shield-alert",
      textColor: "text-purple"
    },
    {
      key: "Eligible",
      title: "Eligible",
      subtitle: "Clear Candidates",
      count: listsData.Eligible.length,
      color: "#22c55e",
      bgColor: "#f0fdf4",
      icon: "circle-check",
      textColor: "text-success"
    },
  ];

  // Tab Configuration
  const tabs = [
    { key: "MasterData", label: `Master Data (${listsData.MasterData.length})`, color: "#1e40af" },
    { key: "ListA", label: `List A (${listsData.ListA.length})`, color: "#ef4444" },
    { key: "ListB", label: `List B (${listsData.ListB.length})`, color: "#f59e0b" },
    { key: "ListC", label: `List C (${listsData.ListC.length})`, color: "#f97316" },
    { key: "ListD", label: `List D (${listsData.ListD.length})`, color: "#a855f7" },
    { key: "Eligible", label: `Eligible (${listsData.Eligible.length})`, color: "#22c55e" },
  ];

  const handleExclude = (record: EmployeeRow) => {
    setSelectedCandidateForInclude(record);
    setIncludeRemarks("");
    setRemarksError(false);
  };

  const handleConfirmInclude = async () => {
    if (!selectedCandidateForInclude || !selectedCycleId) return;
    if (!includeRemarks.trim()) {
      setRemarksError(true);
      return;
    }

    try {
      setIncludingEmpId(selectedCandidateForInclude.id);
      const payload = {
        cycleId: Number(selectedCycleId),
        empId: selectedCandidateForInclude.empNo,
        remarks: includeRemarks.trim()
      };
      const response = await Api.post(API_URLS.PROMOTION.INCLUDE_ELIGIBLE, payload, true);
      console.log("Include response:", response);
      toast.success(`${selectedCandidateForInclude.name} has been added to DPC review.`);
      setSelectedCandidateForInclude(null);
      setIncludeRemarks("");
      setRemarksError(false);
      fetchChecks();
    } catch (error: any) {
      console.error("Failed to include candidate", error);
      toast.error("Failed to include candidate: " + Api.getErrorMessage(error));
    } finally {
      setIncludingEmpId(null);
    }
  };

  // Table Columns Configuration
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: "Sr. No.",
        dataIndex: "sr",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 65
      },
      { title: "Emp No.", dataIndex: "empNo" },
      { title: "Name", dataIndex: "name", render: (v: string) => <div style={{ fontWeight: 600 }}>{v}</div> },
      { title: "Category", dataIndex: "category" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Employee Department", dataIndex: "departmentName" },
      { title: "Exclusion Reason", dataIndex: "exclusionReason", render: (value: string) => value ? <div style={{ minWidth: 180, maxWidth: 280, whiteSpace: "normal" }}>{value}</div> : "-" },
      { title: "Date of Joining", dataIndex: "joiningDate" },
      { title: "Last Promo Date", dataIndex: "lastPromoDate" },
      { title: "Service (Years)", dataIndex: "serviceYears" },
      {
        title: "APAR Total",
        dataIndex: "aparTotal",
        render: (v: string, record: EmployeeRow) => {
          const isFailed = record.apar_evaluation?.status === "FAILED" || record.apar_evaluation?.isSuitable === false;
          return <span className={isFailed ? "text-danger fw-bold" : "text-success fw-bold"}>{formatAparScore(v)}</span>;
        }
      },
      {
        title: "APAR Remarks",
        dataIndex: "aparRemarks",
        render: (v: string, record: EmployeeRow) => {
          const remarks = v || record.aparRemarks || record.apar_remarks || record.aparRemark || record.apar_remark || "—";
          return (
            <div style={{ maxWidth: "220px", whiteSpace: "normal", wordBreak: "break-word" }}>
              {remarks}
            </div>
          );
        }
      },
      {
        title: "D&AR",
        dataIndex: "dar",
        render: (_: any, record: EmployeeRow) => {
          const darBadge = getDarBadgeDetails(record);
          return <span className={`badge ${darBadge.badgeClass}`} style={darBadge.badgeStyle}>{darBadge.text}</span>;
        }
      },
      {
        title: "Vigilance",
        dataIndex: "vigilance",
        render: (_: any, record: EmployeeRow) => {
          const vigBadge = getVigilanceBadgeDetails(record);
          return <span className={`badge ${vigBadge.badgeClass}`} style={vigBadge.badgeStyle}>{vigBadge.text}</span>;
        }
      },
      { title: "Promotion Effective Date", dataIndex: "effectiveDate" },
      {
        title: "Evaluation Details",
        dataIndex: "evaluationDetails",
        render: (_: any, record: EmployeeRow) => (
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
              style={{ fontSize: "11px", borderRadius: "5px", whiteSpace: "nowrap" }}
              onClick={() => {
                setSelectedEvaluationCandidate(record);
                setActiveModalTab("apar");
              }}
            >
              <i className="ti ti-eye fs-6"></i> View Details
            </button>
            {activeList === "ListA" &&
              !record.includedInDpc && !record.included_in_dpc &&
              currentCycle?.status !== "COMPETENT_AUTHORITY_APPROVAL" &&
              currentCycle?.status !== "APPROVED" &&
              currentCycle?.status !== "COMPLETED" && (
                <button
                  type="button"
                  className="btn btn-xs btn-outline-success d-inline-flex align-items-center gap-1 py-1 px-2"
                  onClick={() => handleExclude(record)}
                  style={{ fontSize: '11px', height: '24px', whiteSpace: 'nowrap' }}
                >
                  <i className="ti ti-check"></i> Include in DPC
                </button>
              )}
          </div>
        )
      }
    ];

    if (activeList === "MasterData") {
      cols.push({
        title: "Classification",
        dataIndex: "listClass",
        render: (v: string) => {
          const raw = (v || "").toUpperCase();
          let label = v || "—";
          let badgeClass = "bg-secondary";
          if (raw === "ELIGIBLE_DPC" || raw === "ELIGIBLE") {
            label = "Eligible";
            badgeClass = "bg-success";
          } else if (raw === "LIST_A") {
            label = "List A";
            badgeClass = "bg-danger";
          } else if (raw === "LIST_B") {
            label = "List B";
            badgeClass = "bg-warning text-dark";
          } else if (raw === "LIST_C") {
            label = "List C";
            badgeClass = "bg-warning text-dark";
          } else if (raw === "LIST_D" || raw === "LIST_D_E" || raw === "LIST_E") {
            label = "List D";
            badgeClass = "bg-info text-white";
          }
          return <span className={`badge ${badgeClass}`}>{label}</span>;
        }
      });
    }

    return cols;
  }, [pagination, selectedCycleId, activeList, currentCycle]);

  const renderStatusBadge = (status: string) => {
    const raw = status || "";
    const formatted = raw.replace(/_/g, " ");
    let badgeClass = "bg-soft-info text-info border-info-subtle";

    if (raw === "APPROVED" || raw === "COMPLETED") {
      badgeClass = "bg-soft-success text-success border-success-subtle";
    } else if (raw === "INITIATED" || raw === "ELIGIBILITY_CALCULATED") {
      badgeClass = "bg-soft-warning text-warning border-warning-subtle";
    } else if (raw === "COMPETENT_AUTHORITY_APPROVAL") {
      badgeClass = "bg-soft-success text-success border-success-subtle";
    } else if (raw === "DPC_REVIEW" || raw === "DPC_REVIEW_PENDING") {
      badgeClass = "bg-soft-primary text-primary border-primary-subtle";
    }

    return (
      <span
        className={`badge ${badgeClass} px-2.5 py-1.5 border fw-semibold`}
        style={{
          fontSize: "11px",
          whiteSpace: "nowrap",
          display: "inline-block",
          textAlign: "center",
          letterSpacing: "0.3px"
        }}
      >
        {formatted}
      </span>
    );
  };

  const getResumeStep = (status: string) => {
    switch (status) {
      case "INITIATED": return 2;
      case "ELIGIBILITY_CALCULATED": return 3;
      case "APAR_CALCULATED": return 4;
      case "DAR_CALCULATED": return 5;
      case "VIGILANCE_CALCULATED":
      case "ELIGIBLE_LIST_GENERATED":
      case "COMPLETED": return 6;
      default: return 6;
    }
  };

  const renderActionButton = (cycle: any) => {
    const s = cycle?.status;
    if (s === "COMPETENT_AUTHORITY_APPROVAL") {
      return (
        <Link
          to={`${all_routes.PostPromotion.DpcApprovalList}?cycleId=${cycle.id}`}
          className="btn btn-success btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "32px", fontSize: "12px" }}
        >
          <i className="ti ti-check" style={{ fontSize: "14px" }}></i> Competent Authority Review
        </Link>
      );
    }
    if (s === "DPC_REVIEW_PENDING" || s === "DPC_REVIEW") {
      return (
        <Link
          to={`${all_routes.PostPromotion.DpcReview}?cycleId=${cycle.id}`}
          className="btn btn-primary btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "32px", fontSize: "12px" }}
        >
          <i className="ti ti-file-search" style={{ fontSize: "14px" }}></i> DPC Review
        </Link>
      );
    }
    if (s === "APPROVED" || s === "OFFICE_ORDER_GENERATED" || s === "COMPLETED") {
      return (
        <Link
          to={`${all_routes.PostPromotion.OfficeOrders}?cycleId=${cycle.id}`}
          className="btn btn-info btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
          style={{ borderRadius: "6px", height: "32px", fontSize: "12px" }}
        >
          <i className="ti ti-file-text" style={{ fontSize: "14px" }}></i> Office Orders
        </Link>
      );
    }
    return (
      <Link
        to={`${all_routes.PostPromotion.newRegistration}?cycleId=${cycle.id}&step=${getResumeStep(s)}`}
        className="btn btn-warning btn-sm text-white px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1 text-nowrap"
        style={{ borderRadius: "6px", height: "32px", fontSize: "12px" }}
      >
        <i className="ti ti-player-play" style={{ fontSize: "14px" }}></i> Resume Setup
      </Link>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 mb-3">
          <div className="my-auto">
            <h3 className="page-title mb-1">Promotion Lists (A-E)</h3>
            <p className="text-muted small mb-0">Screening lists for eligibility and exceptions</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-sm-auto justify-content-start justify-content-sm-end">
            <select
              className="form-select form-select-sm flex-fill flex-sm-grow-0"
              value={selectedCycleId}
              onChange={(e) => handleCycleChange(Number(e.target.value))}
              style={{ minWidth: 200, height: 36, maxWidth: 300 }}
            >
              {cyclesList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {/* <button className="btn btn-outline-secondary btn-sm" style={{ height: 36, whiteSpace: 'nowrap' }}>Export PDF</button> */}
            <Link
              to={all_routes.PostPromotion.newRegistration}
              className="btn btn-danger btn-sm text-nowrap d-inline-flex align-items-center justify-content-center flex-fill flex-sm-grow-0"
              style={{ height: 36 }}
            >
              New Promotion Cycle
            </Link>
          </div>
        </div>

        {currentCycle && currentCycle.status && currentCycle.name && currentCycle.name.trim() !== "" && currentCycle.status !== 'COMPLETED' && (
          <div className="card mb-4 border shadow-none" style={{ borderRadius: "10px", overflow: "hidden" }}>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-nowrap mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Active Cycle</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Financial Year</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Cadre</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Pay Scales</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }}>Current Status</th>
                      <th style={{ fontSize: "11px", textTransform: "uppercase", padding: "10px 16px", fontWeight: 700, color: "#475569" }} className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "12px 16px" }} className="fw-bold text-dark">
                        {currentCycle.name || `Cycle #${currentCycle.id}`}
                      </td>
                      <td style={{ padding: "12px 16px" }} className="text-secondary">{currentCycle.financialYear}</td>
                      <td style={{ padding: "12px 16px" }} className="text-secondary">{currentCycle.cadre || 'N/A'}</td>
                      <td style={{ padding: "12px 16px" }} className="text-secondary">
                        {(() => {
                          try {
                            const parsed = typeof currentCycle.grades === 'string' ? JSON.parse(currentCycle.grades) : currentCycle.grades;
                            return Array.isArray(parsed) ? parsed.join(", ") : (currentCycle.grades || "N/A");
                          } catch {
                            return currentCycle.grades || "N/A";
                          }
                        })()}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        {renderStatusBadge(currentCycle.status)}
                      </td>
                      <td style={{ padding: "12px 16px" }} className="text-end">
                        {renderActionButton(currentCycle)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "10px"
          }}
        >
          {promotionOverview.map((sec) => {
            const isActive = activeList === sec.key;
            return (
              <div
                key={sec.key}
                onClick={() => setActiveList(sec.key)}
                className="p-2 px-3 rounded-3 position-relative overflow-hidden"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isActive
                    ? "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                    : "0 1px 3px rgba(0,0,0,0.02)",
                  transform: isActive ? "translateY(-4px)" : "translateY(0)"
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 10px rgba(0, 0, 0, 0.04)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.02)";
                  } else {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)";
                  }
                }}
              >
                {/* Left accent strip */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    background: sec.color
                  }}
                />

                {/* Header row with Title and Icon */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span
                    className="fw-bold text-truncate me-1"
                    style={{
                      fontSize: "11px",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: isActive ? "#1f2937" : "#6b7280"
                    }}
                  >
                    {sec.title}
                  </span>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "22px",
                      height: "22px",
                      background: sec.bgColor,
                      color: sec.color
                    }}
                  >
                    <i className={`ti ti-${sec.icon}`} style={{ fontSize: "11px" }}></i>
                  </div>
                </div>

                {/* Subtitle */}
                <div className="text-secondary mb-1 text-truncate" style={{ fontSize: "10px", fontWeight: 500 }}>
                  {sec.subtitle}
                </div>

                {/* Metric Value count */}
                <div
                  className="h4 mb-0"
                  style={{
                    fontWeight: 700,
                    color: sec.color,
                    fontSize: "18px"
                  }}
                >
                  {sec.count}
                </div>
              </div>
            );
          })}
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="mb-3 fw-bold text-dark">Employee Lists</h5>
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-1.5">
                {tabs.map(t => {
                  const isActive = activeList === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveList(t.key)}
                      className="btn transition-all text-nowrap"
                      style={{
                        backgroundColor: isActive ? t.color : "#f8f9fa",
                        color: isActive ? "#ffffff" : "#495057",
                        borderColor: isActive ? t.color : "#dee2e6",
                        fontWeight: isActive ? 600 : 500,
                        fontSize: "12px",
                        padding: "6px 14px",
                        borderRadius: "6px"
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Toolbar: Filters and Export Excel for Master Data */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 p-2.5 rounded border" style={{ backgroundColor: "#f8fafc" }}>
              <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                {/* Search Box */}
                <div className="position-relative" style={{ minWidth: "220px" }}>
                  <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ps-2 text-muted" style={{ fontSize: "14px" }}></i>
                  <input
                    type="text"
                    className="form-control form-control-sm ps-4"
                    placeholder="Search by Emp No, Name..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ height: "34px", fontSize: "13px" }}
                  />
                </div>

                {activeList === "MasterData" && (
                  <>
                    {/* Status Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small fw-medium">Status:</span>
                      <select
                        className="form-select form-select-sm"
                        value={masterStatusFilter}
                        onChange={(e) => setMasterStatusFilter(e.target.value)}
                        style={{ height: "34px", fontSize: "12px", minWidth: "130px" }}
                      >
                        <option value="ALL">All Statuses</option>
                        <option value="ELIGIBLE_DPC">Eligible</option>
                        <option value="LIST_A">List A (Service)</option>
                        <option value="LIST_B">List B (APAR)</option>
                        <option value="LIST_C">List C (DAR)</option>
                        <option value="LIST_D">List D (Vigilance)</option>
                      </select>
                    </div>

                    {/* DAR Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small fw-medium">DAR:</span>
                      <select
                        className="form-select form-select-sm"
                        value={masterDarFilter}
                        onChange={(e) => setMasterDarFilter(e.target.value)}
                        style={{ height: "34px", fontSize: "12px", minWidth: "110px" }}
                      >
                        <option value="ALL">All DAR</option>
                        <option value="Clear">Clear</option>
                        <option value="Pending">Pending</option>
                      </select>
                    </div>

                    {/* Vigilance Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small fw-medium">Vigilance:</span>
                      <select
                        className="form-select form-select-sm"
                        value={masterVigilanceFilter}
                        onChange={(e) => setMasterVigilanceFilter(e.target.value)}
                        style={{ height: "34px", fontSize: "12px", minWidth: "110px" }}
                      >
                        <option value="ALL">All Vigilance</option>
                        <option value="Clear">Clear</option>
                        <option value="Pending">Pending</option>
                      </select>
                    </div>

                    {/* Reset Button */}
                    {(query || masterStatusFilter !== "ALL" || masterDarFilter !== "ALL" || masterVigilanceFilter !== "ALL") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                        onClick={() => {
                          setQuery("");
                          setMasterStatusFilter("ALL");
                          setMasterDarFilter("ALL");
                          setMasterVigilanceFilter("ALL");
                        }}
                        style={{ height: "34px", fontSize: "12px" }}
                      >
                        <i className="ti ti-rotate-clockwise"></i> Reset
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Export Master Excel Button */}
              {activeList === "MasterData" && (
                <button
                  type="button"
                  className="btn btn-sm btn-success d-flex align-items-center gap-1 px-3 fw-medium text-white shadow-sm"
                  onClick={handleExportMasterExcel}
                  style={{ height: "34px", fontSize: "12px", borderRadius: "6px" }}
                >
                  <i className="ti ti-file-spreadsheet" style={{ fontSize: "16px" }}></i> Export Master Excel
                </button>
              )}
            </div>

            <Table
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              Selection={false}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
            />
          </div>
        </div>

        {/* Include Candidate Modal */}
        {selectedCandidateForInclude && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
                <div className="modal-header bg-warning text-white border-0 py-3" style={{ borderTopLeftRadius: "14px", borderTopRightRadius: "14px" }}>
                  <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2" style={{ fontSize: "16px" }}>
                    <i className="ti ti-user-plus fs-5"></i> Include in DPC
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedCandidateForInclude(null)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <p className="text-dark mb-3" style={{ fontSize: "14px", lineHeight: "1.5" }}>
                    Revalidate and include <strong>{selectedCandidateForInclude.name}</strong> in DPC review if eligible or permitted as an APAR exception.
                  </p>

                  <div className="mb-2 text-start">
                    <label className="form-label fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                      Remarks <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${remarksError ? "is-invalid" : ""}`}
                      rows={3}
                      placeholder="Reason for DPC inclusion"
                      value={includeRemarks}
                      onChange={(e) => {
                        setIncludeRemarks(e.target.value);
                        if (e.target.value.trim()) setRemarksError(false);
                      }}
                      style={{ fontSize: "13px", borderRadius: "8px" }}
                    />
                    {remarksError && (
                      <div className="invalid-feedback">Remarks are required for including a candidate.</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light btn-sm border px-3 fw-semibold"
                    style={{ borderRadius: "6px" }}
                    onClick={() => setSelectedCandidateForInclude(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning text-white btn-sm px-3 fw-semibold d-inline-flex align-items-center gap-1"
                    style={{ borderRadius: "6px" }}
                    disabled={includingEmpId === selectedCandidateForInclude.id}
                    onClick={handleConfirmInclude}
                  >
                    {includingEmpId === selectedCandidateForInclude.id ? "Including..." : "Include in DPC"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Evaluation Details Modal */}
        {selectedEvaluationCandidate && (() => {
          const c = selectedEvaluationCandidate;
          const showExcludeRemark = activeList === "ListA" && (c.listClassification || c.list_classification || c.listClass) === "LIST_A";
          const exclusionReason = c.exclusionReason || c.exclusion?.reason
            || c.checkResults?.service?.details?.manualExclusion?.reason
            || c.check_results?.service?.details?.manualExclusion?.reason;

          const empIdVal = String(c.empNo || c.empId || c.emp_no || "—");
          const empNameVal = String(c.name || c.employeeName || c.empName || c.emp_name || "—");

          // Parse APAR scores
          let aparScores: Record<string, any> = {};
          const rawAparScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
          if (typeof rawAparScores === "string") {
            try {
              aparScores = JSON.parse(rawAparScores);
            } catch (e) {
              aparScores = {};
            }
          } else if (rawAparScores && typeof rawAparScores === "object") {
            aparScores = rawAparScores;
          }

          // D&AR status details
          const dar = getDarBadgeDetails(c);
          const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? "").toString().trim();
          const darStatusLower = rawDarStatus.toLowerCase();
          const isChargeSheet = darStatusLower === "chargesheet issued" || darStatusLower === "chargesheet_issued" || darStatusLower === "chargesheetissued";
          const isMinor = darStatusLower === "minor penalty" || darStatusLower === "minor_penalty" || darStatusLower === "minorpenalty";
          const isMajor = darStatusLower === "major penalty" || darStatusLower === "major_penalty" || darStatusLower === "majorpenalty";
          const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

          const darCaseNo = c.caseNo ?? c.case_no ?? c.dar_evaluation?.caseNo ?? "—";
          const darChargeSheetDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.dar_evaluation?.chargeSheetIssuedDate);
          const darPenaltyDesc = c.penaltyDescription ?? c.penalty_description ?? c.dar_evaluation?.penaltyDescription ?? "—";
          const darDateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? c.dar_evaluation?.dateOfAction);
          const darPenaltyStart = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? c.dar_evaluation?.penaltyStartDate);
          const darPenaltyEnd = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? c.dar_evaluation?.penaltyEndDate);

          // Vigilance details
          const vigStatus = c.vigilance_evaluation?.status ?? c.vigilanceStatus ?? c.vigilance_status ?? "—";
          const vigClearedStr = c.vigilance_evaluation?.vigilanceCleared !== undefined
            ? (c.vigilance_evaluation.vigilanceCleared ? "Cleared" : "Not Cleared")
            : (c.vigilanceCleared !== undefined ? (c.vigilanceCleared ? "Cleared" : "Not Cleared") : "—");

          const crimStatus = c.criminal_evaluation?.status ?? c.criminalStatus ?? c.criminal_status ?? "—";
          const crimClearedStr = c.criminal_evaluation?.criminalCleared !== undefined
            ? (c.criminal_evaluation.criminalCleared ? "Cleared" : "Not Cleared")
            : (c.criminalCleared !== undefined ? (c.criminalCleared ? "Cleared" : "Not Cleared") : "—");

          const vigType = c.vigilanceType ?? c.vigilance_type ?? "—";
          const crimCaseVal = c.criminalCase !== undefined
            ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : (c.criminalCase === "no" || c.criminalCase === "No" ? "No" : String(c.criminalCase)))
            : (c.criminal_case !== undefined ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : (c.criminal_case === "no" || c.criminal_case === "No" ? "No" : String(c.criminal_case))) : "—");
          const vigRemarks = c.vigilanceRemarks ?? c.vigilance_remarks ?? "—";

          return (
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
              onClick={() => setSelectedEvaluationCandidate(null)}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={{ maxWidth: "620px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
                  {/* Header */}
                  <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                    <div>
                      <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                        <i className="ti ti-file-analytics text-primary fs-5"></i>
                        Candidate Evaluation Details
                      </h6>
                      <div className="text-muted small mt-1">
                        Employee: <span className="fw-bold text-dark">{empNameVal}</span> ({empIdVal})
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedEvaluationCandidate(null)}
                      aria-label="Close"
                    ></button>
                  </div>

                  {/* Navigation Tabs */}
                  <div className="px-4 pt-3 bg-white border-bottom">
                    <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-1 mb-0 flex-nowrap" style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                      <li className="nav-item flex-shrink-0">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "apar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("apar")}
                        >
                          <i className="ti ti-file-text me-1.5"></i> APAR Details
                        </button>
                      </li>
                      <li className="nav-item flex-shrink-0">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("dar")}
                        >
                          <i className="ti ti-scale me-1.5"></i> D&AR Details
                        </button>
                      </li>
                      <li className="nav-item flex-shrink-0">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("vigilance")}
                        >
                          <i className="ti ti-shield-check me-1.5"></i> Vigilance Details
                        </button>
                      </li>
                      {showExcludeRemark && (
                        <li className="nav-item flex-shrink-0">
                          <button type="button"
                            className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "exclude" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                            style={{ fontSize: "13px" }}
                            onClick={() => setActiveModalTab("exclude")}>
                            <i className="ti ti-user-minus me-1.5"></i> Exclude Remark
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Body Content */}
                  <div className="modal-body p-4" style={{ minHeight: "260px" }}>
                    {/* Tab 1: APAR Details */}
                    {activeModalTab === "apar" && (
                      <div>
                        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                          Financial Year APAR Marks
                        </h6>
                        {Object.keys(aparScores).length === 0 ? (
                          <div className="alert alert-light border text-muted small p-3 rounded">
                            No individual financial year APAR scores provided.
                          </div>
                        ) : (
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "14px" }}>
                              <thead style={{ backgroundColor: "#f1f5f9" }}>
                                <tr>
                                  <th className="py-2.5 px-3" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>Financial Year</th>
                                  <th className="py-2.5 px-3 text-end" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>APAR Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(aparScores).map(([fy, score]) => {
                                  const rawScoreVal = typeof score === "object" && score !== null ? (score.score ?? score.marks ?? score.value ?? score.rating) : score;
                                  const numericScore = parseFloat(String(rawScoreVal));
                                  const isZero = isNaN(numericScore) || numericScore === 0;

                                  return (
                                    <tr key={fy}>
                                      <td className="py-2.5 px-3 fw-semibold text-dark" style={{ fontSize: "14px" }}>{fy}</td>
                                      <td className="py-2.5 px-3 text-end fw-bold" style={{ fontSize: "16px", color: isZero ? "#64748b" : "#2563eb" }}>
                                        {isZero ? "-" : String(rawScoreVal ?? "-")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="row g-3 mt-1">
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Total APAR Score</div>
                              <div className="fw-bold mt-1" style={{ fontSize: "22px", lineHeight: "1.2", color: (c.apar_evaluation?.status === "FAILED" || c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {formatAparScore(c.apar_evaluation?.totalScore ?? c.aparTotalScore ?? c.apar_total_score ?? c.aparTotal)}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Required Benchmark</div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: "22px", lineHeight: "1.2" }}>
                                {c.apar_evaluation?.requiredBenchmark ?? c.aparBenchmark ?? "32.00"}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Result</div>
                              <div>
                                <span className={`badge ${c.apar_evaluation?.status === "FAILED" ? "bg-danger" : "bg-success"}`} style={{ fontSize: "12px", padding: "5px 12px", fontWeight: 700 }}>
                                  {c.apar_evaluation?.status ?? c.aparStatus ?? "PASSED"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Suitability</div>
                              <div className="fw-bold" style={{ fontSize: "15px", color: (c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {c.apar_evaluation?.isSuitable !== undefined
                                  ? (c.apar_evaluation.isSuitable ? "Suitable" : "Not Suitable")
                                  : (c.isSuitable !== undefined ? (c.isSuitable ? "Suitable" : "Not Suitable") : "Suitable")}
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Remarks</div>
                              <div className="fw-semibold text-dark" style={{ fontSize: "13px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {c.aparRemarks ?? c.apar_remarks ?? c.aparRemark ?? c.apar_remark ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: D&AR Details */}
                    {activeModalTab === "dar" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">D&AR Status</span>
                          <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
                            {dar.text}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Case No.</span>
                          <span className="fw-semibold text-dark">{darCaseNo}</span>
                        </div>

                        {(isChargeSheet || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                            <span className="fw-semibold text-dark">{darChargeSheetDate}</span>
                          </div>
                        )}

                        {(isMinor || isMajor || isClearOrNone) && (
                          <div className="pb-2 border-bottom">
                            <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                            <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                              {darPenaltyDesc}
                            </div>
                          </div>
                        )}

                        {(isMinor || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Date of Action</span>
                            <span className="fw-semibold text-dark">{darDateOfAction}</span>
                          </div>
                        )}

                        {(isMajor || isClearOrNone) && (
                          <>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty Start Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyStart}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty End Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyEnd}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Tab 3: Vigilance Details */}
                    {activeModalTab === "vigilance" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Status</span>
                          <span className="fw-semibold text-dark">{vigStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Cleared</span>
                          <span className={`badge ${vigClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {vigClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Status</span>
                          <span className="fw-semibold text-dark">{crimStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Cleared</span>
                          <span className={`badge ${crimClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {crimClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Type</span>
                          <span className="fw-semibold text-dark">{vigType}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case</span>
                          <span className="fw-semibold text-dark">{crimCaseVal}</span>
                        </div>

                        <div className="pb-2">
                          <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                          <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                            {vigRemarks}
                          </div>
                        </div>
                      </div>
                    )}
                    {showExcludeRemark && activeModalTab === "exclude" && (
                      <div>
                        <h6 className="fw-bold text-dark mb-3">DPC Exclusion Remark</h6>
                        <div className="p-3 rounded bg-light border text-dark" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {exclusionReason || "No DPC exclusion remark recorded. This employee is in List A based on the service evaluation."}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="modal-footer py-2.5 px-4 bg-light border-top d-flex justify-content-end" style={{ borderRadius: "0 0 14px 14px" }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm px-4 fw-semibold"
                      style={{ borderRadius: "6px" }}
                      onClick={() => setSelectedEvaluationCandidate(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default PromotionList;
